import { EyeIcon, EyeOffIcon } from '@langchain/untitled-ui-icons';
import {
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
} from '@mui/joy';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import useSWR from 'swr';

import { useCopy } from '@/components/CopyButton/useCopy';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { SecretInput } from '@/components/SecretInput';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/Table';
import useToast from '@/components/Toast/useToast';
import { Button, Tooltip } from '@/design-system';
import { usePermissions } from '@/hooks/usePermissions.tsx';
import { useTenantSecrets, useTenantSecretsMutation } from '@/hooks/useSwr';
import { TenantSecret } from '@/types/schema';
import { cn } from '@/utils/tailwind';

const pluralRules = new Intl.PluralRules('en');

function CreateSecretModal(props: { open: boolean; onClose: () => void }) {
  'use no memo';
  const form = useForm<TenantSecret>();
  const mutation = useTenantSecretsMutation({
    onSuccess: () => props.onClose(),
  });

  return (
    <Modal open={props.open} onClose={props.onClose}>
      <ModalDialog>
        <ModalClose />
        <div className="mb-4 flex flex-col gap-3">
          <h2 className="text-lg font-semibold">Add secret</h2>
        </div>

        <form
          className="flex flex-col gap-4 overflow-auto"
          onSubmit={form.handleSubmit((secret) =>
            mutation.trigger({ json: [secret] })
          )}
        >
          <FormControl>
            <FormLabel>Key</FormLabel>
            <Input
              type="text"
              className="input"
              placeholder="Enter key"
              sx={{
                fontFamily: "'Fira Code', monospace",
                '& input': {
                  boxShadow: 'none !important',
                },
              }}
              {...form.register('key')}
            />
          </FormControl>

          <FormControl>
            <FormLabel>Value</FormLabel>
            <SecretInput {...form.register('value')} />
          </FormControl>

          {mutation.error && <ExpandableErrorAlert error={mutation.error} />}
          <div className="grid grid-cols-[auto,1fr] gap-2">
            <Button
              type="button"
              variant="outlined"
              color="secondary"
              onClick={props.onClose}
            >
              Cancel
            </Button>

            <Button type="submit" loading={form.formState.isSubmitting}>
              Save secret
            </Button>
          </div>
        </form>
      </ModalDialog>
    </Modal>
  );
}

function DeleteEvaluatorSecret(props: { secret: TenantSecret }) {
  const { createToast } = useToast();
  const mutation = useTenantSecretsMutation({
    onSuccess: () => {
      createToast({
        title: 'Secret deleted',
        description: `The secret ${props.secret.key} has been deleted.`,
      });
    },
  });

  return (
    <Button
      type="button"
      size="sm"
      variant="outlined"
      color="error"
      loading={mutation.isMutating}
      onClick={() =>
        mutation.trigger({ json: [{ key: props.secret.key, value: null }] })
      }
    >
      Delete
    </Button>
  );
}

function DeletePlaygroundSecret(props: {
  secret: TenantSecret;
  onSuccess?: () => void;
}) {
  const { createToast } = useToast();

  return (
    <Button
      type="button"
      size="sm"
      variant="outlined"
      color="error"
      onClick={() => {
        localStorage.removeItem(`ls:${props.secret.key}`);
        createToast({
          title: 'Secret deleted',
          description: `The secret ${props.secret.key} has been deleted.`,
        });
        props.onSuccess?.();
      }}
    >
      Delete
    </Button>
  );
}

function EvaluatorSecretsList() {
  const secrets = useTenantSecrets();

  const nSecrets = secrets.data?.length ?? 0;
  return (
    <div className="rounded-lg border border-secondary">
      <div className="border-b border-secondary px-5 py-4">
        {
          {
            one: `1 secret`,
            two: `${nSecrets} secrets`,
            few: `${nSecrets} secrets`,
            other: `${nSecrets} secrets`,
          }[pluralRules.select(nSecrets)]
        }
      </div>

      <Table>
        <TableHeader>
          <TableRow className={cn(nSecrets === 0 && 'border-b-0')}>
            <TableHead className="pl-5">Key</TableHead>
            <TableHead className="pr-5 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {secrets.data?.map((secret, idx) => {
            return (
              <TableRow key={idx}>
                <TableCell className="pl-5">{secret.key}</TableCell>
                <TableCell className="pr-5 text-right">
                  <DeleteEvaluatorSecret secret={secret} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function PlaygroundSecretReveal(props: { value: string }) {
  const [visible, setVisible] = useState(false);
  const copy = useCopy({ copy: props.value });

  const placeholder = '';
  return (
    <div className="grid w-[300px] grid-cols-[auto,1fr] items-center gap-2 overflow-hidden text-ellipsis">
      <button
        type="button"
        onClick={() => setVisible((visible) => !visible)}
        className="rounded-md p-1 transition-colors hover:bg-secondary-hover active:bg-secondary"
      >
        {!visible ? (
          <EyeIcon className="size-4" />
        ) : (
          <EyeOffIcon className="size-4" />
        )}
      </button>
      {visible ? (
        <Tooltip title={copy.copied ? 'Copied' : 'Click to copy'}>
          <button
            type="button"
            className="flex-shrink overflow-hidden text-ellipsis whitespace-nowrap text-left font-mono"
            onClick={copy.onCopy}
          >
            {visible ? props.value : placeholder}
          </button>
        </Tooltip>
      ) : (
        <span>•••••••••••••••</span>
      )}
    </div>
  );
}

function PlaygroundSecretsList() {
  const secrets = useSWR('/playground/secrets', () => {
    const result: { key: string; value: string }[] = [];
    for (let i = 0; i < localStorage.length; ++i) {
      const rawKey = localStorage.key(i);

      if (
        rawKey?.startsWith('ls:') &&
        rawKey.slice('ls:'.length).toLocaleUpperCase() ===
          rawKey.slice('ls:'.length)
      ) {
        const key = rawKey.slice('ls:'.length);

        if (key.toLocaleUpperCase() === key && !key.includes(':')) {
          const value = localStorage.getItem(rawKey);
          if (value != null) result.push({ key, value });
        }
      }
    }

    return result;
  });

  const nSecrets = secrets.data?.length ?? 0;

  return (
    <div className="rounded-lg border border-secondary">
      <div className="border-b border-secondary px-5 py-4">
        {
          {
            one: `1 secret`,
            two: `${nSecrets} secrets`,
            few: `${nSecrets} secrets`,
            other: `${nSecrets} secrets`,
          }[pluralRules.select(nSecrets)]
        }
      </div>

      <Table>
        <TableHeader>
          <TableRow className={cn(nSecrets === 0 && 'border-b-0')}>
            <TableHead className="pl-5">Key</TableHead>
            <TableHead>Value</TableHead>
            <TableHead className="pr-5 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {secrets.data?.map((secret, idx) => {
            return (
              <TableRow key={idx}>
                <TableCell className="pl-5">{secret.key}</TableCell>
                <TableCell className="font-mono">
                  <PlaygroundSecretReveal value={secret.value} />
                </TableCell>
                <TableCell className="pr-5 text-right">
                  <DeletePlaygroundSecret
                    secret={secret}
                    onSuccess={() => secrets.mutate()}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

export function OrganizationSecrets() {
  const [secretOpen, setSecretOpen] = useState(false);
  const { authorize } = usePermissions();

  return (
    <div className="flex flex-col gap-8 overflow-auto">
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-[1fr,auto] items-center gap-2">
          <div className="flex flex-grow flex-col gap-1">
            <h2 className={cn('font-semibold', 'text-lg')}>
              Workspace Secrets
            </h2>
            <p className="text-sm text-tertiary">
              Server-side secrets used for online evaluations and in the
              playground.
            </p>
          </div>
          {authorize('workspaces:manage') && (
            <Button
              size="sm"
              color="primary"
              onClick={() => setSecretOpen(true)}
            >
              Add secret
            </Button>
          )}
          {secretOpen && (
            <CreateSecretModal open onClose={() => setSecretOpen(false)} />
          )}
        </div>
        <EvaluatorSecretsList />
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <h2 className={cn('font-semibold', 'text-lg')}>Browser Secrets</h2>
          <p className="text-sm text-tertiary">
            Browser-stored secrets used for invoking playground.
          </p>
        </div>

        <PlaygroundSecretsList />
      </div>
    </div>
  );
}
